import { useState, useCallback } from "react";
// @ts-expect-error - papaparse doesn't have types
import Papa from "papaparse";
import { toast } from "sonner";
import settingsTemplate from "../constants/settingsTemplate.json";
import { SETTING_COMPONENT_MAP, SettingsRow, SettingsData, SettingsCategory, AI_SETTING_CONFIG, SETTING_KEYS } from "../constants/settingsStructure";
import { uploadSettingsConfig, getSettingsConfig } from "@/network/network";

export interface ValidationError {
  type: "error" | "warning";
  message: string;
  details?: string[];
}

export const useSettings = () => {
  const [isFetching, setIsFetching] = useState(false);

  /**
   * Merges received settings with template to ensure all required SETTING_KEYS are present
   * @param receivedSettings - Settings data received from API
   * @returns Merged settings data with missing keys filled from template
   */
  const mergeSettingsWithTemplate = useCallback((receivedSettings: SettingsData): SettingsData => {
    // Get all settingIds from the received settings
    const receivedSettingIds = new Set<string>();
    Object.values(receivedSettings).forEach((settingsArray) => {
      settingsArray.forEach((row) => {
        receivedSettingIds.add(row.settingId);
      });
    });

    // Get all required settingIds from SETTING_KEYS
    const requiredSettingIds = Object.values(SETTING_KEYS);

    // Find missing settingIds
    const missingSettingIds = requiredSettingIds.filter((id) => !receivedSettingIds.has(id));

    // If no missing keys, return the received settings as-is
    if (missingSettingIds.length === 0) {
      return receivedSettings;
    }

    console.warn(`Found ${missingSettingIds.length} missing settings, merging from template:`, missingSettingIds, 'CSV needs to be updated');

    // Create a deep copy of received settings to avoid mutation
    const mergedSettings: SettingsData = JSON.parse(JSON.stringify(receivedSettings));

    // For each missing settingId, find it in the template and add it
    const templateData = settingsTemplate as unknown as SettingsData;

    missingSettingIds.forEach((missingId) => {
      // Search through all IntentAreaIds in the template
      Object.entries(templateData).forEach(([intentAreaId, settingsArray]) => {
        settingsArray.forEach((row) => {
          if (row.settingId === missingId) {
            // Initialize the IntentAreaId array if it doesn't exist
            if (!mergedSettings[intentAreaId]) {
              mergedSettings[intentAreaId] = [];
            }
            // Add the missing setting from template
            mergedSettings[intentAreaId].push(row);
          }
        });
      });
    });

    return mergedSettings;
  }, []);

  /**
   * Saves settings JSON to backend via blob upload
   * @param data - Settings data object grouped by settingId
   */
  const saveSettings = useCallback(async (data: SettingsData): Promise<void> => {
    try {
      const jsonString = JSON.stringify(data, null, 2);

      // Create a blob from JSON data
      const blob = new Blob([jsonString], { type: "application/json" });

      // Create a File object from the blob
      const file = new File([blob], "settings-config.json", { type: "application/json" });

      // Upload to backend
      const response = await uploadSettingsConfig(file);

      if (response.data?.url) {
        toast.success("Settings saved successfully");
      }
    } catch (error: any) {
      console.error("Error saving settings to backend:", error);

      // Handle specific error cases
      if (error?.response?.status === 403) {
        toast.error("Permission denied. Only superusers can save settings.");
      } else if (error?.response?.status === 401) {
        toast.error("Authentication required. Please log in again.");
      } else {
        const errorMessage = error?.response?.data?.detail || "Failed to save settings. Please try again.";
        toast.error(errorMessage);
      }

      throw error;
    }
  }, []);

  /**
   * Fetches settings from backend API and returns as JSON
   * Returns merged settings with template to ensure all required keys are present
   */
  const getSettings = useCallback(async (): Promise<SettingsData> => {
    setIsFetching(true);
    try {
      const response = await getSettingsConfig();

      if (response.data.message) {
        throw new Error("No record found")
      }

      /** API returns a presigned URL for the settings config file */
      if (response.data?.url) {
        const presignedUrlResponse = await fetch(response.data.url);
        const receivedData = await presignedUrlResponse.json();

        // Merge received settings with template to fill in any missing keys
        const mergedData = mergeSettingsWithTemplate(receivedData as SettingsData);
        return mergedData;
      }

      return {};
    } catch (error: any) {
      // Handle 404 (no config exists yet) - return empty object silently
      if (error?.response?.status === 404) {
        console.log("No settings config found on backend, using template");
        return {};
      }

      // Handle 401 (authentication error)
      if (error?.response?.status === 401) {
        console.error("Authentication required to load settings");
        toast.error("Authentication required. Please log in again.");
        return {};
      }

      // Handle other errors
      console.error("Error fetching settings from backend:", error);
      return {};
    } finally {
      setIsFetching(false);
    }
  }, [mergeSettingsWithTemplate]);

  /**
   * Converts array of settings rows to grouped object by IntentAreaId
   * Groups settings into arrays by their IntentAreaId
   * Allows the same settingId to exist in different IntentAreaIds
   * @param rows - Array of settings rows
   * @returns Grouped settings data object
   */
  const groupSettingsByIntentArea = useCallback((rows: SettingsRow[]): SettingsData => {
    const grouped: SettingsData = {};
    rows.forEach((row) => {
      if (row.settingId && row.IntentAreaId) {
        // Initialize IntentAreaId array if it doesn't exist
        if (!grouped[row.IntentAreaId]) {
          grouped[row.IntentAreaId] = [];
        }
        // Add the setting to this IntentAreaId's array
        grouped[row.IntentAreaId].push(row);
      }
    });
    return grouped;
  }, []);

  /**
   * Generates a CSV template file based on settingsTemplate.json
   * and automatically downloads it
   */
  const getTemplateFile = useCallback((): void => {
    try {
      // Flatten the grouped template structure to array for CSV export
      const templateArray: SettingsRow[] = [];
      const templateData = settingsTemplate as unknown as SettingsData;

      Object.values(templateData).forEach((settingsArray) => {
        templateArray.push(...settingsArray);
      });

      // Convert JSON template to CSV
      const csv = Papa.unparse(templateArray, {
        columns: ["IntentAreaId", "IntentAreaName", "IntentAreaIcon", "settingId", "settingName", "settingSubtitle", "settingScope", "settingKeywords"]
      });

      // Create blob and download
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      const url = URL.createObjectURL(blob);

      link.setAttribute("href", url);
      link.setAttribute("download", "swivu-settings-template.csv");
      link.style.visibility = "hidden";

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error generating template file:", error);
      throw error;
    }
  }, []);

  /**
   * Exports current settings data to CSV file
   * @param data - Settings data to export (if null/empty, exports template instead)
   */
  const exportSettingsToCSV = useCallback(
    (data: SettingsData | null): void => {
      try {
        if (!data || Object.keys(data).length === 0) {
          // If no data, download template
          getTemplateFile();
          return;
        }

        // Flatten the structure to array for CSV export
        const dataArray: SettingsRow[] = [];
        Object.values(data).forEach((settingsArray) => {
          dataArray.push(...settingsArray);
        });

        const csv = Papa.unparse(dataArray, {
          columns: ["IntentAreaId", "IntentAreaName", "IntentAreaIcon", "settingId", "settingName", "settingSubtitle", "settingScope", "settingKeywords"]
        });

        // Create blob and download
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", "settings-export.csv");
        link.style.visibility = "hidden";

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        URL.revokeObjectURL(url);
      } catch (error) {
        console.error("Error exporting CSV:", error);
        throw error;
      }
    },
    [getTemplateFile]
  );

  /**
   * Validates CSV data against required structure and rules
   * @param rows - Parsed CSV rows
   * @param fileHeaders - Column headers from CSV file
   * @returns Array of validation errors (empty if valid)
   */
  const validateCSVData = useCallback((rows: SettingsRow[], fileHeaders: string[]): ValidationError[] => {
    const errors: ValidationError[] = [];
    const validSettingIds = Object.values(SETTING_KEYS);

    // Required columns
    const requiredColumns = ["IntentAreaId", "IntentAreaName", "IntentAreaIcon", "settingId", "settingName", "settingSubtitle", "settingScope", "settingKeywords"];

    // Check if all required columns exist
    const missingColumns = requiredColumns.filter((col) => !fileHeaders.includes(col));

    if (missingColumns.length > 0) {
      errors.push({
        type: "error",
        message: "Missing required columns",
        details: missingColumns.map((col) => `Column "${col}" is required`)
      });
    }

    // Validate each row
    const invalidSettingIds: string[] = [];
    const emptySettingIds: number[] = [];
    const presentSettingIds = new Set<string>();
    const rowsWithEmptyCells: Array<{ row: number; fields: string[] }> = [];

    rows.forEach((row, index) => {
      const rowNumber = index + 2; // +2 because index starts at 0 and row 1 is header

      // Check for empty settingId
      if (!row.settingId || row.settingId.trim() === "") {
        emptySettingIds.push(rowNumber);
        return; // Skip further validation for this row
      }

      // Check for empty required fields
      const emptyFields: string[] = [];
      if (!row.IntentAreaId || row.IntentAreaId.trim() === "") {
        emptyFields.push("IntentAreaId");
      }
      if (!row.IntentAreaName || row.IntentAreaName.trim() === "") {
        emptyFields.push("IntentAreaName");
      }
      if (!row.IntentAreaIcon || row.IntentAreaIcon.trim() === "") {
        emptyFields.push("IntentAreaIcon");
      }
      if (!row.settingName || row.settingName.trim() === "") {
        emptyFields.push("settingName");
      }
      if (!row.settingSubtitle || row.settingSubtitle.trim() === "") {
        emptyFields.push("settingSubtitle");
      }
      if (!row.settingScope || row.settingScope.trim() === "") {
        emptyFields.push("settingScope");
      }
      if (!row.settingKeywords || row.settingKeywords.trim() === "") {
        emptyFields.push("settingKeywords");
      }

      if (emptyFields.length > 0) {
        rowsWithEmptyCells.push({ row: rowNumber, fields: emptyFields });
      }

      // Track which settingIds are present in the CSV
      presentSettingIds.add(row.settingId);

      // Check if settingId is valid
      if (!validSettingIds.includes(row.settingId)) {
        invalidSettingIds.push(`"${row.settingId}" (row ${rowNumber})`);
      }
    });

    // Check for missing required settingIds
    const missingSettingIds = validSettingIds.filter((id) => !presentSettingIds.has(id));

    if (missingSettingIds.length > 0) {
      errors.push({
        type: "error",
        message: `Missing required settingId values (${missingSettingIds.length} total)`,
        details: missingSettingIds.slice(0, 10).map((id) => `"${id}"`)
      });

      if (missingSettingIds.length > 10) {
        errors.push({
          type: "error",
          message: `... and ${missingSettingIds.length - 10} more missing settingId(s)`
        });
      }
    }

    // Add errors for rows with empty cells
    if (rowsWithEmptyCells.length > 0) {
      errors.push({
        type: "error",
        message: `${rowsWithEmptyCells.length} row(s) have empty required fields`,
        details: rowsWithEmptyCells.slice(0, 10).map(({ row, fields }) => `Row ${row}: Missing ${fields.join(", ")}`)
      });

      if (rowsWithEmptyCells.length > 10) {
        errors.push({
          type: "error",
          message: `... and ${rowsWithEmptyCells.length - 10} more row(s) with empty fields`
        });
      }
    }

    // Add errors for empty settingIds
    if (emptySettingIds.length > 0) {
      errors.push({
        type: "warning",
        message: `${emptySettingIds.length} row(s) with empty settingId will be skipped`,
        details: emptySettingIds.slice(0, 10).map((row) => `Row ${row}`)
      });
    }

    // Add errors for invalid settingIds
    if (invalidSettingIds.length > 0) {
      errors.push({
        type: "error",
        message: `Invalid settingId values found`,
        details: invalidSettingIds.slice(0, 10) // Show first 10
      });

      if (invalidSettingIds.length > 10) {
        errors.push({
          type: "error",
          message: `... and ${invalidSettingIds.length - 10} more invalid settingId(s)`
        });
      }
    }

    return errors;
  }, []);

  /**
   * Imports and validates CSV file
   * @param file - CSV file to import
   * @returns Promise resolving to object with grouped settings data and validation errors
   * @throws Error if critical validation fails (file type, size, empty file, missing columns)
   */
  const importSettingsFromCSV = useCallback(
    async (file: File): Promise<{ data: SettingsData; errors: ValidationError[] }> => {
      // Validate file type
      if (!file.name.endsWith(".csv")) {
        throw new Error("Please select a CSV file");
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        throw new Error("File size must be less than 5MB");
      }

      // Transform CSV file with header information
      const parseResult = await new Promise<{ data: SettingsRow[]; headers: string[] }>((resolve, reject) => {
        Papa.parse(file, {
          header: true,
          skipEmptyLines: true,
          complete: (results: any) => {
            try {
              const data = results.data as SettingsRow[];
              const headers = results.meta?.fields || [];
              resolve({ data, headers });
            } catch (error) {
              reject(new Error("Failed to parse CSV data"));
            }
          },
          error: (error: any) => {
            reject(error);
          }
        });
      });

      const { data: rows, headers } = parseResult;

      if (rows.length === 0) {
        throw new Error("CSV file is empty or contains no valid data rows");
      }

      // Validate CSV data
      const validationErrors = validateCSVData(rows, headers);

      // Check for critical errors (missing columns or invalid settingIds)
      const criticalErrors = validationErrors.filter((err) => err.type === "error");

      if (criticalErrors.length > 0) {
        // If there are critical errors, throw them
        const errorMessage = criticalErrors
          .map((err) => {
            if (err.details) {
              return `${err.message}:\n${err.details.join("\n")}`;
            }
            return err.message;
          })
          .join("\n\n");

        throw new Error(errorMessage);
      }

      // Filter out rows with empty settingIds
      const validRows = rows.filter((row) => row.settingId && row.settingId.trim() !== "");

      // Group by IntentAreaId then settingId (last occurrence wins for duplicates within same IntentAreaId)
      const groupedData = groupSettingsByIntentArea(validRows);

      return {
        data: groupedData,
        errors: validationErrors
      };
    },
    [validateCSVData, groupSettingsByIntentArea]
  );

  /**
   * Converts CSV data (SettingsData) to SettingsCategory structure
   * Data is already grouped by IntentAreaId, so we iterate through each area
   * Merges AI-specific configuration from AI_SETTING_CONFIG for AI prompt settings
   * @param csvData - Settings data grouped by IntentAreaId with arrays of settings
   * @param componentMap - Map of settingId to React component
   * @returns Array of settings categories
   */
  const convertCsvDataToCategories = useCallback((csvData: SettingsData, componentMap: typeof SETTING_COMPONENT_MAP): SettingsCategory[] => {
    const categories: SettingsCategory[] = [];

    // Iterate through each IntentAreaId
    Object.entries(csvData).forEach(([intentAreaId, settingsArray]) => {
      // Get the first setting to extract category metadata
      const firstSetting = settingsArray[0];

      if (!firstSetting) return;

      const category: SettingsCategory = {
        id: intentAreaId,
        label: firstSetting.IntentAreaName,
        icon: firstSetting.IntentAreaIcon,
        subcategories: []
      };

      // Add all settings in this IntentAreaId as subcategories
      // Create unique IDs by combining settingId with index to support duplicate settingIds with different names
      settingsArray.forEach((row, index) => {
        const Component = componentMap[row.settingId as keyof typeof componentMap];

        if (Component) {
          // Get AI-specific config if it exists for this setting
          const aiConfig = AI_SETTING_CONFIG[row.settingId] || {};

          // Create unique id by combining intentAreaId, settingId, and index
          // This allows the same settingId to appear multiple times with different names
          const uniqueId = `${intentAreaId}-${row.settingId}-${index}`;

          category.subcategories.push({
            id: uniqueId,
            label: row.settingName,
            description: row.settingSubtitle,
            component: Component,
            settingScope: row.settingScope as "personal" | "board",
            settingId: row.settingId,
            terms: row.settingKeywords ? row.settingKeywords.split(",").map((k) => k.trim()) : [],
            // Merge AI-specific configuration
            ...aiConfig
          });
        }
      });

      categories.push(category);
    });

    return categories;
  }, []);

  /**
   * Gets categories from settings data
   * Uses CSV data if available, otherwise falls back to template data
   * @param csvData - CSV settings data from localStorage
   * @param templateData - Template settings data
   * @returns Array of settings categories
   */
  const getCategories = useCallback(
    (csvData?: SettingsData | null, templateData?: SettingsData | null): SettingsCategory[] => {
      // Use CSV data from localStorage if available
      if (csvData && Object.keys(csvData).length > 0) {
        return convertCsvDataToCategories(csvData, SETTING_COMPONENT_MAP);
      }

      // Otherwise, use template JSON data
      if (templateData && Object.keys(templateData).length > 0) {
        return convertCsvDataToCategories(templateData, SETTING_COMPONENT_MAP);
      }

      // Return empty array if no data available
      return [];
    },
    [convertCsvDataToCategories]
  );

  return {
    saveSettings,
    getSettings,
    isFetching,
    getTemplateFile,
    exportSettingsToCSV,
    importSettingsFromCSV,
    getCategories
  };
};
